import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Eye, EyeOff, CheckCircle, Clock, AlertTriangle, Users, MapPin, Flag, Activity, UserX, UserCheck, Shield, ClipboardList, FileEdit, Send, Bell, BellOff, MessageSquare, Power } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { getSpots, deleteSpot, getReports, updateReportStatus, getStats, getUsers, updateUserStatus, updateUserRole, promoteUserModerator, deleteUserAdmin, maskSpot, unmaskSpot, publishAdminMessage, sendBroadcastEmail, getAdminMessages, updateAdminMessage, deleteAdminMessage } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  
  const [spots, setSpots] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [spotFilter, setSpotFilter] = useState({ hasReports: "", search: "", status: "" });
  const [spotSort, setSpotSort] = useState({ key: "reports_count", dir: "desc" });
  const [reportSort, setReportSort] = useState({ key: "status", dir: "asc" });
  const [userSort, setUserSort] = useState({ key: "created_at", dir: "asc" });
  const [copiedUserId, setCopiedUserId] = useState(null);
  const [messageContent, setMessageContent] = useState("");
  const [messagePublishStatus, setMessagePublishStatus] = useState("idle");
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastStatus, setBroadcastStatus] = useState("idle");
  const [broadcastCount, setBroadcastCount] = useState(0);
  const [activeTab, setActiveTab] = useState("reports");
  const [isImportant, setIsImportant] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [messageHistory, setMessageHistory] = useState([]);
  const [messageHistoryLoading, setMessageHistoryLoading] = useState(false);
  const [selectedHistoryMessage, setSelectedHistoryMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editExpiresAt, setEditExpiresAt] = useState("");

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      toast.error("Accès réservé aux administrateurs");
      navigate("/");
      return;
    }
    if (isAdmin) {
      loadData();
    }
  }, [authLoading, isAdmin, navigate]);

  const loadData = async () => {
    try {
      const [spotsData, reportsData, usersData, statsData] = await Promise.all([
        getSpots({ includeAll: true, applyFuzz: false }),
        getReports(),
        getUsers(),
        getStats(),
      ]);
      setSpots(spotsData);
      setReports(reportsData);
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleDeleteSpot = async (spotId) => {
    if (!window.confirm("Supprimer ce spot définitivement?")) return;
    try {
      await deleteSpot(spotId);
      toast.success("Spot supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdateReportStatus = async (reportId, status) => {
    try {
      await updateReportStatus(reportId, status);
      toast.success("Statut mis à jour");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "disabled" : "active";
    try {
      await updateUserStatus(userId, newStatus);
      toast.success(newStatus === "active" ? "Utilisateur activé" : "Utilisateur désactivé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleUserRole = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    if (!window.confirm(`Changer le rôle en ${newRole}?`)) return;
    try {
      await updateUserRole(userId, newRole);
      toast.success("Rôle mis à jour");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleModeratorRole = async (userId, currentRole) => {
    const action = currentRole === "moderator" ? "rétrograder en utilisateur" : "promouvoir modérateur";
    if (!window.confirm(`Voulez-vous ${action} cet utilisateur ?`)) return;
    try {
      await promoteUserModerator(userId);
      toast.success("Rôle mis à jour");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteUser = async (userId, email) => {
  const ok = window.confirm(
    `Supprimer définitivement cet utilisateur ?\n\n- Email libéré: ${email}\n- Spots / marques: anonymisés comme une suppression de compte\n\nAction irréversible.`
  );
  if (!ok) return;

  try {
    await deleteUserAdmin(userId);
    toast.success("Utilisateur supprimé définitivement");
    loadData();
  } catch (error) {
    toast.error("Erreur lors de la suppression");
  }
};

  const handleMaskSpot = async (spotId) => {
    try {
      await maskSpot(spotId);
      toast.success("Spot masqué");
      loadData();
    } catch (error) {
      toast.error("Erreur lors du masquage");
    }
  };

  const handleUnmaskSpot = async (spotId) => {
    try {
      await unmaskSpot(spotId);
      toast.success("Spot rendu visible");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'opération");
    }
  };


  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="text-amber-600 border-amber-300"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case "reviewed":
        return <Badge variant="outline" className="text-blue-600 border-blue-300"><Eye className="w-3 h-3 mr-1" />Examiné</Badge>;
      case "resolved":
        return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Résolu</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSpotStatusBadge = (status) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline" className="text-amber-600 border-amber-300"><FileEdit className="w-3 h-3 mr-1" />Brouillon</Badge>;
      case "visible":
        return <Badge variant="outline" className="text-green-600 border-green-300"><Eye className="w-3 h-3 mr-1" />Visible</Badge>;
      case "masked":
        return <Badge variant="destructive"><EyeOff className="w-3 h-3 mr-1" />Masqué</Badge>;
      // Legacy statuses
      case "public":
        return <Badge variant="outline" className="text-blue-600 border-blue-300">Public (ancien)</Badge>;
      case "private":
        return <Badge variant="secondary">Privé (ancien)</Badge>;
      case "hidden":
        return <Badge variant="destructive">Caché (ancien)</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const CATEGORY_BADGE_CLASS = {
    facile:     "border-emerald-500/30 bg-emerald-500/15 text-emerald-200",
    audacieux:  "border-amber-500/30 bg-amber-500/15 text-amber-200",
    temporaire: "border-red-500/30 bg-red-500/15 text-red-200",
  };

  const getCategoryBadge = (category) => (
    <Badge variant="outline" className={`text-xs capitalize ${CATEGORY_BADGE_CLASS[category] || "border-white/20 bg-white/5 text-white/80"}`}>
      {category}
    </Badge>
  );

  const getSpotById = (spotId) => spots.find(s => s.id === spotId);
  const getUserById = (userId) => users.find(u => u.id === userId);

  // Filter spots
  const filteredSpots = spots.filter(spot => {
    if (spotFilter.hasReports === "yes" && spot.reports_count === 0) return false;
    if (spotFilter.hasReports === "no" && spot.reports_count > 0) return false;
    if (spotFilter.status && spot.status !== spotFilter.status) return false;
    if (spotFilter.search && !spot.title.toLowerCase().includes(spotFilter.search.toLowerCase())) return false;
    return true;
  });

  const handlePublishMessage = async () => {
    if (!messageContent.trim()) return;
    const ok = window.confirm(
      "Ce message sera affiché à tous les utilisateurs à leur prochaine connexion. Confirmer ?"
    );
    if (!ok) return;
    setMessagePublishStatus("publishing");
    try {
      await publishAdminMessage(messageContent.trim(), isImportant, expiresAt ? new Date(expiresAt).toISOString() : null);
      setMessagePublishStatus("done");
      setMessageContent("");
      setIsImportant(false);
      setExpiresAt("");
      loadMessageHistory();
    } catch (error) {
      toast.error("Erreur lors de la publication");
      setMessagePublishStatus("idle");
    }
  };

  const loadMessageHistory = async () => {
    setMessageHistoryLoading(true);
    try {
      const data = await getAdminMessages();
      setMessageHistory(data);
    } catch (_) {}
    setMessageHistoryLoading(false);
  };

  useEffect(() => {
    if (activeTab === "message") loadMessageHistory();
  }, [activeTab]);

  const handleToggleMessage = async (m) => {
    try {
      await updateAdminMessage(m.id, { active: !m.active });
      loadMessageHistory();
    } catch (_) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteMessage = async (m) => {
    if (!window.confirm("Supprimer ce message définitivement ?")) return;
    try {
      await deleteAdminMessage(m.id);
      loadMessageHistory();
    } catch (_) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleEditMessage = async () => {
    try {
      await updateAdminMessage(selectedHistoryMessage.id, {
        content: editContent,
        expires_at: editExpiresAt ? new Date(editExpiresAt).toISOString() : null,
      });
      setSelectedHistoryMessage(null);
      setEditingMessage(false);
      loadMessageHistory();
    } catch (_) {
      toast.error("Erreur lors de la modification");
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastBody.trim()) return;
    const ok = window.confirm(
      "Cet email sera envoyé à tous les membres vérifiés. Confirmer ?"
    );
    if (!ok) return;
    setBroadcastStatus("sending");
    try {
      const result = await sendBroadcastEmail(broadcastSubject.trim(), broadcastBody.trim());
      setBroadcastCount(result.recipient_count);
      setBroadcastStatus("done");
      setBroadcastSubject("");
      setBroadcastBody("");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du broadcast");
      setBroadcastStatus("idle");
    }
  };

  const STATUS_ORDER = { pending: 0, reviewed: 1, resolved: 2 };

  const toggleReportSort = (key) => {
    setReportSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const sortedReports = [...reports].sort((a, b) => {
    const spot_a = getSpotById(a.spot_id);
    const spot_b = getSpotById(b.spot_id);
    let av, bv;
    if (reportSort.key === "spot")         { av = (spot_a?.title ?? "").toLowerCase();          bv = (spot_b?.title ?? "").toLowerCase(); }
    else if (reportSort.key === "creator") { av = (spot_a?.creator_pseudo ?? "").toLowerCase(); bv = (spot_b?.creator_pseudo ?? "").toLowerCase(); }
    else if (reportSort.key === "reason")  { av = a.reason.toLowerCase();                       bv = b.reason.toLowerCase(); }
    else /* status */                      { av = STATUS_ORDER[a.status] ?? 99;                 bv = STATUS_ORDER[b.status] ?? 99; }
    if (av < bv) return reportSort.dir === "asc" ? -1 : 1;
    if (av > bv) return reportSort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSpotSort = (key) => {
    setSpotSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const sortedSpots = [...filteredSpots].sort((a, b) => {
    let av, bv;
    if (spotSort.key === "title")          { av = a.title.toLowerCase();                  bv = b.title.toLowerCase(); }
    else if (spotSort.key === "category")  { av = a.category ?? "";                       bv = b.category ?? ""; }
    else if (spotSort.key === "creator")   { av = (a.creator_pseudo ?? "").toLowerCase(); bv = (b.creator_pseudo ?? "").toLowerCase(); }
    else if (spotSort.key === "photos")    { av = a.photos?.length ?? 0;                  bv = b.photos?.length ?? 0; }
    else if (spotSort.key === "reports")   { av = a.reports_count ?? 0;                   bv = b.reports_count ?? 0; }
    else /* status */                      { av = a.status ?? "";                         bv = b.status ?? ""; }
    if (av < bv) return spotSort.dir === "asc" ? -1 : 1;
    if (av > bv) return spotSort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleUserSort = (key) => {
    setUserSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const sortedUsers = [...users].sort((a, b) => {
    let av, bv;
    if (userSort.key === "pseudo")     { av = a.pseudo.toLowerCase(); bv = b.pseudo.toLowerCase(); }
    else if (userSort.key === "role")  { av = a.role;                 bv = b.role; }
    else if (userSort.key === "spots") { av = a.spots_count ?? 0;     bv = b.spots_count ?? 0; }
    else /* created_at */             { av = new Date(a.created_at); bv = new Date(b.created_at); }
    if (av < bv) return userSort.dir === "asc" ? -1 : 1;
    if (av > bv) return userSort.dir === "asc" ? 1 : -1;
    return 0;
  });

  if (authLoading || isInitialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-map-button"
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="heading text-2xl text-slate-900">Administration</h1>
              <p className="text-sm text-slate-500">Gestion des spots, signalements et utilisateurs</p>
            </div>
          </div>
          <Badge variant="secondary" className="gap-1">
            <Shield className="w-3 h-3" />
            {user?.pseudo}
          </Badge>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1"><MapPin className="w-3 h-3" />Total spots</CardDescription>
                <CardTitle className="text-3xl">{stats.spots.total}</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-500">
                {stats.spots.visible} visibles / {stats.spots.masked} masqués
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1"><Users className="w-3 h-3" />Utilisateurs</CardDescription>
                <CardTitle className="text-3xl">{stats.users.total}</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-500">
                {stats.users.active} actifs
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1"><Flag className="w-3 h-3" />Signalements</CardDescription>
                <CardTitle className="text-3xl">{stats.reports.total}</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-slate-500">
                {stats.reports.pending} en attente
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1"><Activity className="w-3 h-3" />Traces</CardDescription>
                <CardTitle className="text-3xl">{stats.traces.total}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="reports" data-testid="reports-tab">
              Signalements ({reports.length})
            </TabsTrigger>
            <TabsTrigger value="spots" data-testid="spots-tab">
              Spots ({spots.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">
              Utilisateurs ({users.length})
            </TabsTrigger>
            <TabsTrigger value="message">
              <MessageSquare className="w-3 h-3 mr-1" />
              Message
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle>Signalements</CardTitle>
                <CardDescription>Gérez les signalements des utilisateurs</CardDescription>
              </CardHeader>
              <CardContent>
                {reports.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucun signalement pour le moment
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleReportSort("spot")}>
                            Spot {reportSort.key === "spot" ? (reportSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleReportSort("creator")}>
                            Créateur {reportSort.key === "creator" ? (reportSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleReportSort("reason")}>
                            Raison {reportSort.key === "reason" ? (reportSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead>Signalé par</TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleReportSort("status")}>
                            Statut {reportSort.key === "status" ? (reportSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedReports.map((report) => {
                          const spot = getSpotById(report.spot_id);
                          const reporter = report.user_id ? getUserById(report.user_id) : null;
                          return (
                            <TableRow key={report.id}>
                              <TableCell className="font-medium">
                                {spot ? (
                                  <div>
                                    <div>{spot.title}</div>
                                    <div className="text-xs">{getCategoryBadge(spot.category)}</div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">Spot supprimé</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {spot?.creator_pseudo || <span className="text-slate-400">-</span>}
                              </TableCell>
                              <TableCell>
                                <div>{report.reason}</div>
                                {report.comment && <div className="text-xs text-slate-500 truncate max-w-32">{report.comment}</div>}
                              </TableCell>
                              <TableCell>
                                {reporter ? reporter.pseudo : (report.email || "Anonyme")}
                              </TableCell>
                              <TableCell>{getStatusBadge(report.status)}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Select
                                    value={report.status}
                                    onValueChange={(v) => handleUpdateReportStatus(report.id, v)}
                                  >
                                    <SelectTrigger className="w-28" data-testid={`report-status-select-${report.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">En attente</SelectItem>
                                      <SelectItem value="reviewed">Examiné</SelectItem>
                                      <SelectItem value="resolved">Résolu</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {spot && (
                                    <Button
                                      data-testid={`delete-reported-spot-${report.id}`}
                                      variant="destructive"
                                      size="icon"
                                      onClick={() => handleDeleteSpot(spot.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Spots Tab */}
          <TabsContent value="spots">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>Tous les spots</CardTitle>
                    <CardDescription>Liste complète des spots (brouillons, visibles, masqués)</CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      placeholder="Rechercher..."
                      value={spotFilter.search}
                      onChange={(e) => setSpotFilter(prev => ({ ...prev, search: e.target.value }))}
                      className="w-40"
                    />
                    <Select
                      value={spotFilter.status || "all"}
                      onValueChange={(v) => setSpotFilter(prev => ({ ...prev, status: v === "all" ? "" : v }))}
                    >
                      <SelectTrigger className="w-36" data-testid="status-filter">
                        <SelectValue placeholder="Statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous statuts</SelectItem>
                        <SelectItem value="draft">Brouillons</SelectItem>
                        <SelectItem value="visible">Visibles</SelectItem>
                        <SelectItem value="masked">Masqués</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={spotFilter.hasReports || "all"}
                      onValueChange={(v) => setSpotFilter(prev => ({ ...prev, hasReports: v === "all" ? "" : v }))}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Signalements" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="yes">Signalés</SelectItem>
                        <SelectItem value="no">Non signalés</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredSpots.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucun spot trouvé
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSpotSort("title")}>
                            Titre {spotSort.key === "title" ? (spotSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSpotSort("category")}>
                            Catégorie {spotSort.key === "category" ? (spotSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSpotSort("creator")}>
                            Créateur {spotSort.key === "creator" ? (spotSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSpotSort("photos")}>
                            Photos {spotSort.key === "photos" ? (spotSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead>Traces</TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSpotSort("reports")}>
                            Signalements {spotSort.key === "reports" ? (spotSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleSpotSort("status")}>
                            Statut {spotSort.key === "status" ? (spotSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedSpots.map((spot) => (
                          <TableRow key={spot.id} className={spot.reports_count > 0 ? "bg-amber-50" : ""}>
                            <TableCell className="font-medium">{spot.title}</TableCell>
                            <TableCell>{getCategoryBadge(spot.category)}</TableCell>
                            <TableCell>
                              {spot.creator_pseudo || <span className="text-slate-400">-</span>}
                            </TableCell>
                            <TableCell>{spot.photos?.length || 0}/2</TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap">
                                <Badge variant="outline" className="text-xs" title="Passés">{spot.traces_passed || 0}P</Badge>
                                <Badge variant="outline" className="text-xs" title="Réguliers">{spot.traces_regular || 0}R</Badge>
                                <Badge variant="outline" className="text-xs" title="Envies">{spot.traces_want_to_try || 0}E</Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              {spot.reports_count > 0 ? (
                                <Badge variant="destructive">{spot.reports_count}</Badge>
                              ) : (
                                <span className="text-slate-400">0</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {getSpotStatusBadge(spot.status)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {spot.status === "visible" && (
                                  <Button
                                    data-testid={`mask-spot-${spot.id}`}
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleMaskSpot(spot.id)}
                                    title="Masquer"
                                  >
                                    <EyeOff className="w-4 h-4" />
                                  </Button>
                                )}
                                {spot.status === "masked" && (
                                  <Button
                                    data-testid={`unmask-spot-${spot.id}`}
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleUnmaskSpot(spot.id)}
                                    title="Rendre visible"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  data-testid={`delete-spot-${spot.id}`}
                                  variant="destructive"
                                  size="icon"
                                  onClick={() => handleDeleteSpot(spot.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>Utilisateurs</CardTitle>
                  <CardDescription>Gestion des comptes utilisateurs</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucun utilisateur
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleUserSort("pseudo")}>
                            Pseudo {userSort.key === "pseudo" ? (userSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleUserSort("role")}>
                            Rôle {userSort.key === "role" ? (userSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleUserSort("spots")}>
                            Spots {userSort.key === "spots" ? (userSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Notifs</TableHead>
                          <TableHead className="cursor-pointer select-none hover:text-slate-900" onClick={() => toggleUserSort("created_at")}>
                            Inscription {userSort.key === "created_at" ? (userSort.dir === "asc" ? "↑" : "↓") : <span className="text-slate-300">↕</span>}
                          </TableHead>
                          <TableHead>Dernière connexion</TableHead>
                          <TableHead>Connexions</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedUsers.map((u) => (
                          <TableRow key={u.id} className={u.status === "disabled" ? "opacity-50" : ""}>
                            <TableCell className="font-medium">
                              <span>{u.pseudo}</span>
                              <span
                                title={u.email}
                                className="ml-1 cursor-pointer text-slate-400 hover:text-slate-600 text-xs transition-colors"
                                onClick={() => {
                                  navigator.clipboard.writeText(u.email);
                                  setCopiedUserId(u.id);
                                  setTimeout(() => setCopiedUserId(null), 1000);
                                }}
                              >
                                {copiedUserId === u.id ? "✓" : "@"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={u.role === "admin" ? "default" : u.role === "moderator" ? "outline" : "secondary"}>
                                {u.role === "admin" && <Shield className="w-3 h-3 mr-1" />}
                                {u.role === "moderator" && <ClipboardList className="w-3 h-3 mr-1" />}
                                {u.role}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">{u.spots_count ?? 0}</TableCell>
                            <TableCell>
                              <Badge variant={u.status === "active" ? "outline" : "destructive"}>
                                {u.status === "active" ? "Actif" : "Désactivé"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {u.notify_spots_enabled
                                ? <Badge variant="outline" className="text-green-600 border-green-300"><Bell className="w-3 h-3 mr-1" />ON</Badge>
                                : <Badge variant="outline" className="text-slate-400 border-slate-300"><BellOff className="w-3 h-3 mr-1" />OFF</Badge>
                              }
                            </TableCell>
                            {(() => {
                              const createdDay = new Date(u.created_at).toLocaleDateString("fr-FR");
                              const lastDay = u.last_login ? new Date(u.last_login).toLocaleDateString("fr-FR") : null;
                              const isOld = (Date.now() - new Date(u.created_at)) > 7 * 24 * 3600 * 1000;
                              const neverReturned = lastDay && lastDay === createdDay && isOld;
                              return (
                                <>
                                  <TableCell className={`text-sm ${neverReturned ? "font-bold text-red-500" : "text-slate-500"}`}>
                                    {createdDay}
                                  </TableCell>
                                  <TableCell className={`text-sm ${neverReturned ? "font-bold text-red-500" : "text-slate-500"}`}>
                                    {lastDay ?? "—"}
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-500">
                                    {u.login_count != null ? u.login_count : "—"}
                                  </TableCell>
                                </>
                              );
                            })()}
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {u.id !== user?.id && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleToggleUserStatus(u.id, u.status)}
                                      title={u.status === "active" ? "Désactiver" : "Activer"}
                                    >
                                      {u.status === "active" ? (
                                        <UserX className="w-4 h-4" />
                                      ) : (
                                        <UserCheck className="w-4 h-4" />
                                      )}
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleToggleUserRole(u.id, u.role)}
                                      title={u.role === "admin" ? "Rétrograder" : "Promouvoir admin"}
                                    >
                                      <Shield className="w-4 h-4" />
                                    </Button>
                                    {u.role !== "admin" && (
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleToggleModeratorRole(u.id, u.role)}
                                        title={u.role === "moderator" ? "Rétrograder modérateur" : "Promouvoir modérateur"}
                                      >
                                        <ClipboardList className="w-4 h-4" />
                                      </Button>
                                    )}

                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      onClick={() => handleDeleteUser(u.id, u.email)}
                                      title="Supprimer définitivement"
                                   >
                                  <Trash2 className="w-4 h-4" />
                                  </Button>

                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Message Tab */}
          <TabsContent value="message">
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>Message aux utilisateurs</CardTitle>
                  <CardDescription>Affiché à tous les utilisateurs à leur prochaine connexion (non fermable)</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-w-xl">
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isImportant}
                        onChange={(e) => setIsImportant(e.target.checked)}
                        className="accent-amber-500"
                      />
                      Message important
                    </label>
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      Non écrasé par les messages suivants. Disparaît uniquement
                      après lecture ou expiration.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <label>Expiration (optionnel)</label>
                    <input
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      className="bg-transparent border border-white/20 rounded px-2 py-1 text-xs text-white/70"
                    />
                  </div>
                  <div>
                    <Textarea
                      value={messageContent}
                      onChange={(e) => {
                        setMessagePublishStatus("idle");
                        setMessageContent(e.target.value.slice(0, 500));
                      }}
                      rows={5}
                      placeholder="Saisir le message à diffuser..."
                      maxLength={500}
                    />
                    <span className="text-xs text-slate-400">{messageContent.length}/500</span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    {messagePublishStatus === "done" && (
                      <span className="text-sm text-green-600">Message publié ✓</span>
                    )}
                    <Button
                      onClick={handlePublishMessage}
                      disabled={!messageContent.trim() || messagePublishStatus === "publishing"}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {messagePublishStatus === "publishing" ? "Publication..." : "Publier"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Historique des messages</CardTitle>
              </CardHeader>
              <CardContent>
                {messageHistoryLoading ? (
                  <p className="text-xs text-slate-500">Chargement...</p>
                ) : messageHistory.length === 0 ? (
                  <p className="text-xs text-slate-500">Aucun message publié.</p>
                ) : (
                  <div className="space-y-1 max-w-xl">
                    {messageHistory.map((m) => (
                      <div
                        key={m.id}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-white/5 transition-colors"
                      >
                        <button
                          onClick={() => { setSelectedHistoryMessage(m); setEditingMessage(false); }}
                          className="flex-1 text-left flex items-center gap-3 min-w-0"
                          title={m.content}
                        >
                          <span className="text-xs text-slate-400 shrink-0">
                            {new Date(m.created_at).toLocaleDateString("fr-FR")}
                          </span>
                          <span className="flex-1 min-w-0 text-xs text-slate-600 truncate">
                            {m.content.length > 100 ? m.content.slice(0, 100) + "…" : m.content}
                          </span>
                          {m.is_important && (
                            <span className="text-[10px] text-amber-400 border border-amber-500/40 rounded px-1 shrink-0">important</span>
                          )}
                          {m.expires_at && (
                            <span className="text-[10px] text-rose-400 border border-rose-500/40 rounded px-1 shrink-0">
                              exp. {new Date(m.expires_at).toLocaleDateString("fr-FR")}
                            </span>
                          )}
                        </button>
                        {m.type !== "welcome" && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleToggleMessage(m)}
                              title={m.active ? "Désactiver" : "Activer"}
                              className="p-1 rounded hover:bg-white/10"
                            >
                              <Power className={`w-3.5 h-3.5 ${m.active ? "text-green-400" : "text-slate-500"}`} />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(m)}
                              title="Supprimer"
                              className="p-1 rounded hover:bg-white/10"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {selectedHistoryMessage && (
              <Dialog open={true} onOpenChange={() => { setSelectedHistoryMessage(null); setEditingMessage(false); }}>
                <DialogContent className="max-w-md bg-[#0f0d0e] text-white border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-white text-sm">
                      Message du {new Date(selectedHistoryMessage.created_at).toLocaleDateString("fr-FR")}
                      {selectedHistoryMessage.is_important && <span className="ml-2 text-xs text-amber-400">(important)</span>}
                    </DialogTitle>
                  </DialogHeader>
                  {editingMessage ? (
                    <div className="space-y-3 mt-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value.slice(0, 500))}
                        rows={5}
                        maxLength={500}
                        className="bg-white/5 border-white/10 text-white text-sm"
                      />
                      <span className="text-xs text-slate-500">{editContent.length}/500</span>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <label>Expiration</label>
                        <input
                          type="datetime-local"
                          value={editExpiresAt}
                          onChange={(e) => setEditExpiresAt(e.target.value)}
                          className="bg-transparent border border-white/20 rounded px-2 py-1 text-xs text-white/70"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" className="border-white/20 text-white/60 hover:bg-white/10" onClick={() => setEditingMessage(false)}>Annuler</Button>
                        <Button size="sm" onClick={handleEditMessage} disabled={!editContent.trim()}>Enregistrer</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap mt-2">
                        {selectedHistoryMessage.content}
                      </div>
                      {selectedHistoryMessage.type !== "welcome" && (
                        <div className="flex justify-end mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-white/20 text-white/60 hover:bg-white/10"
                            onClick={() => {
                              setEditContent(selectedHistoryMessage.content);
                              setEditExpiresAt(selectedHistoryMessage.expires_at
                                ? new Date(selectedHistoryMessage.expires_at).toISOString().slice(0, 16)
                                : "");
                              setEditingMessage(true);
                            }}
                          >
                            <FileEdit className="w-3.5 h-3.5 mr-1" />
                            Modifier
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </DialogContent>
              </Dialog>
            )}

            <Card className="mt-4">
              <CardHeader>
                <div>
                  <CardTitle>Email broadcast</CardTitle>
                  <CardDescription>Envoi d'un email à tous les membres vérifiés</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-w-xl">
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Sujet *</label>
                    <Input
                      value={broadcastSubject}
                      onChange={(e) => {
                        setBroadcastStatus("idle");
                        setBroadcastSubject(e.target.value.slice(0, 200));
                      }}
                      placeholder="Sujet de l'email"
                      maxLength={200}
                    />
                    <span className="text-xs text-slate-400">{broadcastSubject.length}/200</span>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Corps *</label>
                    <Textarea
                      value={broadcastBody}
                      onChange={(e) => {
                        setBroadcastStatus("idle");
                        setBroadcastBody(e.target.value.slice(0, 5000));
                      }}
                      rows={7}
                      placeholder="Corps de l'email..."
                      maxLength={5000}
                    />
                    <span className="text-xs text-slate-400">{broadcastBody.length}/5000</span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    {broadcastStatus === "done" && (
                      <span className="text-sm text-green-600">Email envoyé à {broadcastCount} membres ✓</span>
                    )}
                    <Button
                      onClick={handleSendBroadcast}
                      disabled={!broadcastSubject.trim() || !broadcastBody.trim() || broadcastStatus === "sending"}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {broadcastStatus === "sending" ? "Envoi en cours..." : "Envoyer à tous les membres"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
